import { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import {
  isNumber,
  toNumber,
} from 'lodash';
import {
  ColorUtil,
  Field2 as Field,
  VibeIcon,
  viLock,
  color,
} from 'vibeguide';
import Grid from '@mui/material/Grid';

function SegmentCalculator({
  className = '',
  style = {},
  spotSelectionStrategy = '',
  currencyCode = null,
  cpm = 0,
  budget = 0,
  impressions = 0,
  spotsPerHour = 0,
  locked = false,
  disableInput = false,
  onUpdate = () => {},
  segmentType = 'standard',
}) {
  const [disableFields, setDisableFields] = useState({
    cpm: false,
    budget: false,
    impressions: false,
  });

  const prevSpotSelectionStrategy = useRef(spotSelectionStrategy);

  const allCalculatorFieldsSet = cpm > 0
    && budget > 0
    && impressions > 0
    && (segmentType === 'standard' || segmentType === 'added-value');

  /**
   * Get the name of the disabled field (if one is disabled)
   * Used to tell the user which field will be computed automatically when "Calculate" is clicked
   */
  const getDisabledFieldName = () => {
    if (disableFields.cpm && disableFields.budget && segmentType === 'added-value') {
      return 'CPM and Budget';
    }

    if (disableFields.cpm) {
      return 'CPM';
    }

    if (disableFields.budget) {
      return 'Budget';
    }

    if (disableFields.impressions) {
      return 'Target Impressions';
    }

    return null;
  };

  const disabledFieldName = getDisabledFieldName();

  const onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    onUpdate({
      [name]: value,
    });
  };

  const onChangeCurrency = ({
    name,
    value,
  }) => {
    onUpdate({
      [name]: value,
    });
  };

  /**
   * When the impressions field is changed
   */
  const onBlurImpressions = (e) => {
    const {
      target: {
        value,
      },
    } = e;

    onUpdate({
      impressions: toNumber(value.replace(/\D/g, '')),
    });
  };

  /**
   * Get the value of the impression field
   */
  const getImpressionValue = () => {
    if (isNumber(impressions) && impressions > 0) {
      // format the impressions
      return impressions.toLocaleString();
    }

    if (isNumber(impressions) && impressions <= 0) {
      // do not show a value for 0 impressions
      return '';
    }

    // show whatever is set as the impression value
    return impressions;
  };

  /**
   * Unlock the calculator
   */
  const onClickUnlock = () => {
    onUpdate({
      lockCalculator: false,
    });
  };

  /**
   * Determine whether a field needs to be disabled
   * When 2 of the 3 fields (CPM, Budget, Impressions are filled), disable the last to be calculated by the API
   */
  useEffect(() => {
    let unsetFieldCount = 0;
    if (cpm <= 0) {
      unsetFieldCount += 1;
    }

    if (budget <= 0) {
      unsetFieldCount += 1;
    }

    if (impressions <= 0) {
      unsetFieldCount += 1;
    }

    if (unsetFieldCount === 1) {
      // only one field is left unset, disable it so it can be calculated by the API
      if (cpm <= 0) {
        setDisableFields({
          cpm: true,
          budget: false,
          impressions: false,
        });
      } else if (budget <= 0) {
        setDisableFields({
          cpm: false,
          budget: true,
          impressions: false,
        });
      } else if (impressions <= 0) {
        setDisableFields({
          cpm: false,
          budget: false,
          impressions: true,
        });
      }
    } else if (segmentType === 'added-value') {
      setDisableFields({
        cpm: true,
        budget: true,
        impressions: false,
      });
    } else {
      // more than one field is still unset, ensure all fields are enabled
      setDisableFields({
        cpm: false,
        budget: false,
        impressions: false,
      });
    }
  }, [cpm, budget, impressions, segmentType]);

  useEffect(() => {
    if (prevSpotSelectionStrategy.current !== spotSelectionStrategy) {
      if (spotSelectionStrategy === 'spots-per-hour') {
        onUpdate({
          cpm: '',
          impressions: '',
        });
      } else {
        onUpdate({
          spotsPerHour: '',
        });
      }
      prevSpotSelectionStrategy.current = spotSelectionStrategy;
    }
  }, [spotSelectionStrategy]);

  const spotsPeriodCheck = (event) => {
    if (event.target.name === 'spotsPerHour') {
      if (event.key === '.') {
        event.preventDefault();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keypress', spotsPeriodCheck);

    return () => {
      document.removeEventListener('keypress', spotsPeriodCheck);
    };
  }, []);

  return (
    <div
      className={classNames('SegmentCalculator', className)}
      style={style}
    >
      <div
        className="calculator-container"
        style={{
          position: 'relative',
          padding: 16,
          border: `1px solid ${color.whiteSmoke}`,
        }}
      >
        {spotSelectionStrategy !== 'spots-per-hour' ? (
          <Grid spacing={2} container>
            {segmentType === 'standard' && (
              <Grid size={6}>
                <Field
                  type="currency"
                  label="CPM"
                  name="cpm"
                  value={cpm}
                  currencyProps={{
                    currencyCode,
                  }}
                  disabled={disableInput || locked || disableFields.cpm}
                  tabIndex={29}
                  testId="segment-calculator-cpm-input"
                  required={!disableFields.cpm}
                  onChange={onChangeCurrency}
                />
              </Grid>
            )}

            {segmentType === 'standard' && (
              <Grid size={6}>
                <Field
                  type="currency"
                  label="Budget"
                  name="budget"
                  value={budget}
                  currencyProps={{
                    currencyCode,
                  }}
                  disabled={disableInput || locked || disableFields.budget}
                  tabIndex={30}
                  testId="segment-calculator-budget-input"
                  required={!disableFields.budget}
                  onChange={onChangeCurrency}
                />
              </Grid>
            )}

            <Grid size={6}>
              <Field
                type="text"
                label="Target Impressions"
                name="impressions"
                placeholder="Impressions"
                value={getImpressionValue()}
                disabled={disableInput || locked || disableFields.impressions}
                tabIndex={31}
                testId="segment-calculator-impressions-input"
                required={!disableFields.impressions}
                onChange={onChange}
                onBlur={onBlurImpressions}
              />
            </Grid>

            {!locked && disabledFieldName && segmentType === 'standard' && (
              <Grid size={6}>
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                  }}
                >
                  <div
                    style={{
                      marginBottom: 4,
                      fontWeight: 'bold',
                    }}
                  >
                    {disabledFieldName}
                  </div>

                  will be calculated automatically on save
                </div>
              </Grid>
            )}

            {!locked && allCalculatorFieldsSet && segmentType === 'standard' && (
              <Grid size={6}>
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    color: color.error,
                  }}
                >
                  You must provide exactly two of the following values: Budget, CPM, Impressions
                </div>
              </Grid>
            )}
          </Grid>
        ) : (
          <Grid spacing={2} container>
            <Grid size={6}>
              <Field
                type="number"
                label="Spots Per Hour"
                name="spotsPerHour"
                value={spotsPerHour}
                tabIndex={32}
                testId="segment-calculator-spots-per-hour-input"
                onChange={onChange}
                required
              />
            </Grid>

            {segmentType === 'standard' && (
              <Grid size={6}>
                <Field
                  type="currency"
                  label="Budget"
                  name="budget"
                  value={budget}
                  currencyProps={{
                    currencyCode,
                  }}
                  disabled={disableInput || locked || disableFields.budget}
                  tabIndex={33}
                  testId="segment-calculator-budget-input"
                  required={!disableFields.budget}
                  onChange={onChangeCurrency}
                />
              </Grid>
            )}

            {segmentType === 'standard' && (
              <Grid size={6}>
                <Field
                  type="currency"
                  label="CPM"
                  name="cpm"
                  value={cpm}
                  currencyProps={{
                    currencyCode,
                  }}
                  tabIndex={34}
                  testId="segment-calculator-cpm-input"
                  onChange={onChangeCurrency}
                  disabled
                />
              </Grid>
            )}

            <Grid size={6}>
              <Field
                type="text"
                label="Target Impressions"
                name="impressions"
                placeholder="Impressions"
                value={getImpressionValue()}
                tabIndex={35}
                testId="segment-calculator-impressions-input"
                onChange={onChange}
                onBlur={onBlurImpressions}
                disabled
              />
            </Grid>

          </Grid>
        )}

        {locked && (
          <div
            className="locked"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: ColorUtil.transparent(color.obsidian, 32),
            }}
          >
            <VibeIcon
              icon={viLock}
              type="button"
              buttonProps={{
                size: 32,
                color: color.white,
                borderColor: color.obsidian,
              }}
              tooltip="Unlock Calculator"
              color={color.obsidian}
              size={20}
              onClick={onClickUnlock}
              data-testid="segment-calculator-unlock-button"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default SegmentCalculator;
